import {
  AuthError,
  createAuthQuery,
  createCurrencyQuery,
  createEsimQuery,
} from "@mozioinc/query";
import posthog from "posthog-js";
import { redirectToLogin } from "./auth";
import { getEnvs } from "./env";
import {
  getAccessToken,
  getRefreshToken,
  removeTokens,
  setAccessToken,
  setRefreshToken,
} from "./token";
import { getInitialLocale } from "./translation";
import { getWhiteLabel } from "./white-label";

const { QUERY_ENV, DEV } = getEnvs();

const authQuery = createAuthQuery(() => ({
  ctx: {
    env: QUERY_ENV,
  },
}));

const refreshTokensRetryCounts = new Map<string, number>();
const MAX_RETRY_COUNT = 3;

export const esimQuery = createEsimQuery(() => ({
  ctx: {
    env: QUERY_ENV,
  },
  headers: {
    "x-whitelabel": DEV ? "app-staging.mozio.com" : location.hostname,
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    lang: getInitialLocale(),
    "white-label-id": getWhiteLabel().id,
    "white-label-name": getWhiteLabel().name,
    "partner-ref": getWhiteLabel().partnerRef,
    hostname: location.hostname,
    "distinct-id": posthog.get_distinct_id(),
    Authorization: getAccessToken() ? `JWT ${getAccessToken()}` : undefined,
  },
  postProcess: {
    onError: async ({ options, input, error, fetcher, fetcherName }) => {
      const isAuthError = error instanceof AuthError;
      if (!isAuthError) {
        throw error;
      }

      if (refreshTokensRetryCounts.get(fetcherName) ?? 0 >= MAX_RETRY_COUNT) {
        console.error("Max retry count reached");
        removeTokens();
        redirectToLogin();
        return;
      }

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 0) + 1,
      );

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.error("No refresh token found");
        removeTokens();
        redirectToLogin();
        return;
      }

      const result = await authQuery.refreshTokens(refreshToken);
      if ("error" in result) {
        console.error(result.message);
        removeTokens();
        redirectToLogin();
        return;
      }

      setAccessToken(result.accessToken);
      setRefreshToken(result.refreshToken);
      return await fetcher(options, input as any); // eslint-disable-line @typescript-eslint/no-explicit-any
    },
  },
}));

export const currencyQuery = createCurrencyQuery({
  ctx: {
    env: QUERY_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    lang: getInitialLocale(),
  },
});
